import { RefObject, useCallback, useEffect, useRef } from 'react';
import { selectIsResizing, selectResizingColumn } from '../store/createEagGridStore';
import { useEagGridSelector } from '../store/useEagGridSelector';
import { useEagGridStore } from '../store/useEagGridStore';
import { toIntegerWidth } from '../utils/toIntegerWidth';
const MIN_COLUMN_WIDTH = 50;
type UseColumnResizeReturn = {
  onResizeStart: (colKey: string, startX: number, colIndex: number, resizeFromLeft?: boolean) => void;
  isResizing: boolean;
  resizingColumn: string | null;
};

/**
 * Manages column resize interactions using **direct DOM updates** during drag.
 *
 * The hook owns the full pointer lifecycle: onResizeStart attaches document-level
 * `pointermove` and `pointerup` listeners that are removed automatically on resize end
 * or component unmount. This guarantees pointer events are always received even if the
 * cursor leaves the scroll container (edge case: fast drag).
 *
 * Performance design (Vercel rules: `rerender-use-ref-transient-values` + `js-batch-dom-css`):
 * - During drag: transient width stored in a ref, `<col>` element style updated directly.
 *   Zero React re-renders, zero store notifications.
 * - On pointer-up (primary button only): commits the final width to the store in a single
 *   `setColumnWidth` call + `endResizing` batch.
 *
 * Context menu is suppressed on `document` during the resize session to prevent the browser
 * menu from appearing on accidental right-click.
 *
 * @param colgroupRef — ref to the `<colgroup>` DOM element whose `<col>` children control column widths
 * @param getWidth — returns the current store width for a column key (used to capture startWidth)
 */
export const useColumnResize = (colgroupRef: RefObject<HTMLTableColElement | null>, getWidth: (key: string) => number): UseColumnResizeReturn => {
  const store = useEagGridStore();
  const isResizing = useEagGridSelector(selectIsResizing);
  const resizingColumn = useEagGridSelector(selectResizingColumn) ?? null;

  // Transient refs — all mutable state lives here, not in React state.
  const colKeyRef = useRef<string | null>(null);
  const startXRef = useRef<number>(0);
  const startWidthRef = useRef<number>(0);
  const currentWidthRef = useRef<number>(0);
  // Cache the active <col> element during resize start to avoid indexing into colgroupRef.current
  // inside the hot-path move handler — decouples the callback from the hook argument ref.
  const activeColElementRef = useRef<HTMLElement | null>(null);

  // Store references to document listeners for cleanup.
  const cleanupRef = useRef<(() => void) | null>(null);

  /**
   * @mandatory Passed to useOnMount as the cleanup return — executes on unmount to guarantee
   * document listeners are removed even if the component unmounts during an active resize session.
   * Must have stable identity (useCallback with empty deps) so the cleanup closure always
   * references the latest cleanupRef without re-running useOnMount.
   */
  const removeDocumentListeners = useCallback(() => {
    cleanupRef.current?.();
    cleanupRef.current = null;
  }, []);

  // Stores whether the active resize session uses a left-side handle (right-pinned columns).
  // When true, the delta is inverted: dragging left increases width, dragging right decreases it.
  const resizeFromLeftRef = useRef<boolean>(false);

  /**
   * @mandatory Passed to HeaderRow.onResizeStart which threads it to HeaderCell.
   * onResizeStart is also consumed by useOnMount cleanup (removeDocumentListeners).
   * Without stable identity, HeaderRow would re-render every time any store state changes,
   * defeating React.memo. Deps: getWidth (stable Map-backed fn), store, colgroupRef (refs),
   * removeDocumentListeners (stable useCallback above).
   */
  const onResizeStart = useCallback((colKey: string, startX: number, colIndex: number, resizeFromLeft = false) => {
    removeDocumentListeners();
    const width = getWidth(colKey);
    colKeyRef.current = colKey;
    startXRef.current = startX;
    startWidthRef.current = width;
    currentWidthRef.current = width;
    resizeFromLeftRef.current = resizeFromLeft;
    activeColElementRef.current = colgroupRef.current?.children[colIndex] as HTMLElement | undefined ?? null;
    store.startResizing(colKey);
    const handlePointerMove = (e: PointerEvent) => {
      if (!colKeyRef.current) {
        return;
      }
      const delta = e.clientX - startXRef.current;
      // For left-side handles (right-pinned columns) the delta is inverted:
      // dragging left (negative delta) should increase width,
      // dragging right (positive delta) should decrease width.
      const effectiveDelta = resizeFromLeftRef.current ? -delta : delta;
      const newWidth = toIntegerWidth(Math.max(MIN_COLUMN_WIDTH, startWidthRef.current + effectiveDelta));
      currentWidthRef.current = newWidth;

      // Direct DOM write — browser batches this with the pointer event paint frame.
      const colElement = activeColElementRef.current;
      if (colElement) {
        colElement.style.width = `${newWidth}px`;
      }
    };
    const handlePointerUp = (e: PointerEvent) => {
      // Only end resize on primary (left) button release — ignore right/middle.
      if (e.button !== 0) {
        return;
      }
      if (colKeyRef.current && currentWidthRef.current !== startWidthRef.current) {
        store.setColumnWidth(colKeyRef.current, currentWidthRef.current);
      }
      colKeyRef.current = null;
      activeColElementRef.current = null;
      startXRef.current = 0;
      startWidthRef.current = 0;
      currentWidthRef.current = 0;
      store.endResizing();
      removeDocumentListeners();
    };
    const handleContextMenu = (e: Event) => {
      e.preventDefault();
    };
    document.addEventListener('pointermove', handlePointerMove);
    document.addEventListener('pointerup', handlePointerUp);
    document.addEventListener('contextmenu', handleContextMenu, true);
    cleanupRef.current = () => {
      document.removeEventListener('pointermove', handlePointerMove);
      document.removeEventListener('pointerup', handlePointerUp);
      document.removeEventListener('contextmenu', handleContextMenu, true);
    };
  }, [getWidth, store, colgroupRef, removeDocumentListeners]);

  // Safety: remove document listeners if the component unmounts during an active resize.
  // eslint-disable-next-line no-restricted-syntax -- intentional: cleanup-only useEffect with empty deps; useOnMount does not support cleanup returns; removeDocumentListeners is useCallback([]) so this fires once on mount and tears down on unmount
  useEffect(() => removeDocumentListeners, [removeDocumentListeners]);
  return {
    onResizeStart,
    isResizing,
    resizingColumn
  };
};